import React from "react";
import { Phone, Mail, CalendarCheck } from "lucide-react";
import { usePageMeta } from "@/components/site/usePageMeta";
import { PageHero } from "@/components/site/PageHero";
import { SectionHeading } from "@/components/site/ui";
import { ContactSection } from "@/components/site/ContactSection";
import { LeadForm } from "@/components/site/LeadForm";
import { TrustBadges } from "@/components/site/TrustBadges";
import { ROUTES, CONTACT } from "@/components/site/data";

export default function ContactPage() {
  usePageMeta(
    "Contact | Wallstreet Realty",
    "Contact Wallstreet Realty: search for a home, request a home value, or learn about joining our Southern California real estate and mortgage brokerage."
  );
  return (
    <>
      <PageHero
        crumb="Contact"
        eyebrow="Get In Touch"
        title="Ready to Make Your Next Move?"
        subtitle="Searching for a home, thinking about selling, or looking for a brokerage to grow with, Wallstreet Realty is ready to help."
        ctas={[
          { label: "Search Homes", href: ROUTES.idx, testid: "contact-hero-search" , pill: true},
          { label: "Get My Home Value", href: ROUTES.sell, variant: "gold", testid: "contact-hero-value" },
        ]}
      />
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Send a Message"
            title="How Can We Help?"
            subtitle="Send us a message and we'll get back to you the same business day whenever possible, or skip the form and call us directly."
            center
          />
          <div className="mt-10 grid lg:grid-cols-2 gap-6 items-start">
            <LeadForm variant="contact" testid="contact-form" />
            <div className="space-y-5">
              <div className="bg-[#F5F5F5] border border-[#E5E5E5] rounded-[20px] p-8">
                <div className="w-12 h-12 rounded-xl bg-[#254369]/10 flex items-center justify-center mb-4">
                  <Phone className="text-[#254369]" size={22} />
                </div>
                <p className="font-heading font-bold text-xl text-[#2D2D37]">Call or Text</p>
                <p className="mt-2 text-sm text-[#2D2D37]/65">Fastest way to reach us during business hours.</p>
                <a
                  href={`tel:${CONTACT.phoneTel}`}
                  data-testid="contact-card-phone"
                  className="mt-4 inline-flex items-center gap-2 text-lg font-heading font-bold text-[#254369] hover:text-[#C9A961] transition-colors"
                >
                  {CONTACT.phone}
                </a>
              </div>
              <div className="bg-[#F5F5F5] border border-[#E5E5E5] rounded-[20px] p-8">
                <div className="w-12 h-12 rounded-xl bg-[#254369]/10 flex items-center justify-center mb-4">
                  <Mail className="text-[#254369]" size={22} />
                </div>
                <p className="font-heading font-bold text-xl text-[#2D2D37]">Email</p>
                <p className="mt-2 text-sm text-[#2D2D37]/65">Send documents or detailed questions any time.</p>
                <a
                  href={`mailto:${CONTACT.email}`}
                  data-testid="contact-card-email"
                  className="mt-4 inline-flex items-center gap-2 text-lg font-heading font-bold text-[#254369] hover:text-[#C9A961] transition-colors break-all"
                >
                  {CONTACT.email}
                </a>
              </div>
              <div className="bg-[#F5F5F5] border border-[#E5E5E5] rounded-[20px] p-8">
                <div className="w-12 h-12 rounded-xl bg-[#254369]/10 flex items-center justify-center mb-4">
                  <CalendarCheck className="text-[#254369]" size={22} />
                </div>
                <p className="font-heading font-bold text-xl text-[#2D2D37]">Book a Consultation</p>
                <p className="mt-2 text-sm text-[#2D2D37]/65">
                  Request a call back in the form and tell us the best time.
                  We'll confirm your consultation by text or email.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <ContactSection />
      <TrustBadges />
    </>
  );
}
